/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN EXPENSES MANAGEMENT API
 * Role: Admin
 * Scope: Tenant-based Expenses, Categories, & Last Payee Lookup
 * Standard: Enterprise / MNC Level
 * Endpoint: /admin/finance/expenses.php
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

const ADMIN_EXPENSES_ENDPOINT = "/admin/finance/expenses.php";

export const getAdminExpenses = async (
  { 
    page = 1, 
    limit = 10, 
    search = "", 
    expense_category_id = "", 
    academy_branch_id = "", 
    status = "", 
    payment_method = "", 
    start_date = "", 
    end_date = "" 
  } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_EXPENSES_ENDPOINT,
      null,
      "Admin – Fetch Expenses",
      {
        params: { 
          page, 
          limit, 
          search, 
          expense_category_id, 
          academy_branch_id, 
          status, 
          payment_method, 
          start_date, 
          end_date 
        },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message: res?.message || "Failed to fetch expenses",
        data: [],
        summary: { total_amount: 0 },
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      summary: res.summary || { total_amount: 0 },
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error("Admin Fetch Expenses Error:", error);
    return {
      success: false,
      message: error.message || "Unexpected error occurred",
      data: [],
      summary: { total_amount: 0 },
      meta: {},
    };
  }
};

export const getAdminExpenseById = async (id, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid expense ID" };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_EXPENSES_ENDPOINT}?id=${id}`,
      null,
      "Admin – Fetch Expense By ID",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to fetch expense record" };
    }

    return { success: true, data: res.data };
  } catch (error) {
    console.error("Admin Fetch Expense By ID Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};

export const getAdminExpenseCategories = async (status = "active", options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_EXPENSES_ENDPOINT,
      null,
      "Admin – Fetch Expense Categories",
      {
        params: { action: "categories", status },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to fetch expense categories", data: [] };
    }

    return { success: true, data: res.data || [] };
  } catch (error) {
    console.error("Admin Fetch Expense Categories Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred", data: [] };
  }
};

export const getAdminLastPayeeByCategory = async (expenseCategoryId, options = {}) => {
  try {
    if (!expenseCategoryId) {
      return { success: false, message: "Invalid expense category ID", data: null };
    }

    const res = await apiCall(
      "GET",
      ADMIN_EXPENSES_ENDPOINT,
      null,
      "Admin – Fetch Last Payee Details",
      {
        params: { action: "last_payee", expense_category_id: expenseCategoryId },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to fetch last payee details", data: null };
    }

    return { success: true, data: res.data || null, message: res.message || "" };
  } catch (error) {
    console.error("Admin Fetch Last Payee Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred", data: null };
  }
};

export const createAdminExpense = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_EXPENSES_ENDPOINT,
      payload,
      "Admin – Create Expense",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to record expense" };
    }

    return { success: true, data: res.data, message: res.message };
  } catch (error) {
    console.error("Admin Create Expense Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};

export const updateAdminExpense = async (id, payload, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid expense ID" };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_EXPENSES_ENDPOINT}?id=${id}`,
      payload,
      "Admin – Update Expense",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to update expense record" };
    }

    return { 
      success: true, 
      data: res.data || null, 
      message: res.message || "Expense updated successfully" 
    };
  } catch (error) {
    console.error("Admin Update Expense Error:", error);
    return { success: false, message: error?.message || "Unexpected error occurred" };
  }
};

export const deleteAdminExpense = async (id, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Invalid expense ID" };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_EXPENSES_ENDPOINT}?id=${id}`,
      null,
      "Admin – Delete Expense",
      options
    );

    if (!res || res.status !== true) {
      return { success: false, message: res?.message || "Failed to delete expense record" };
    }

    return { success: true, message: res.message };
  } catch (error) {
    console.error("Admin Delete Expense Error:", error);
    return { success: false, message: error.message || "Unexpected error occurred" };
  }
};